import { Box, Stack, Typography } from "@mui/material";
import {
  CarouselTitleRow,
  CarouselSectionHeader,
  CarouselTrack,
  CarouselNavButton,
} from "./CarouselTrack";
import GlassCard from "Components/GlassCard/GlassCard";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import Loading from "Components/Loading/Loading";
import { useIsMobile } from "Hooks/useIsMobile";

interface ICarouselCardProps {
  title: string | React.ReactNode;
  items: React.ReactNode[];
  loading?: boolean;
  emptyMessage?: string;
  actionSlot?: React.ReactNode;
  inview?: number;
  itemMinWidth?: number;
}

function CarouselCard({
  title,
  items,
  loading,
  emptyMessage,
  actionSlot,
  inview,
  itemMinWidth,
}: ICarouselCardProps) {
  const isMobile = useIsMobile();
  const [trackRef, setTrackRef] = useState<HTMLDivElement | null>(null);
  const [activePage, setActivePage] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  const itemsPerPage = useMemo(() => {
    if (isMobile) {
      return 1;
    }

    // if min width is set, return the minimum number of items that will fit in the viewport
    if (itemMinWidth) {
      return Math.floor((trackRef?.clientWidth || 0) / itemMinWidth);
    }

    if (inview) {
      return inview;
    }
    return items.length;
  }, [isMobile, inview, items.length, itemMinWidth, trackRef]);

  const pageCount = useMemo(() => {
    return itemsPerPage > 0 ? Math.ceil(items.length / itemsPerPage) : 1;
  }, [items.length, itemsPerPage]);

  // todo, calc gaps proerlly
  const scrollAmount = useMemo(() => {
    if (isMobile) {
      return trackRef?.clientWidth || 0;
    }

    const show = itemsPerPage;
    const showingGaps = 16 - show;
    const width = (trackRef?.clientWidth || 0) / show - showingGaps;
    return width;
  }, [trackRef, isMobile, itemsPerPage]);

  const scrollBy = useCallback(
    (direction: "left" | "right") => {
      trackRef?.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    },
    [scrollAmount, trackRef],
  );

  useEffect(() => {
    if (!trackRef) return;

    const updateOverflow = () => {
      setCanScroll(trackRef.scrollWidth > trackRef.clientWidth + 1);
    };

    const updateActivePage = () => {
      const maxScroll = trackRef.scrollWidth - trackRef.clientWidth;
      if (maxScroll <= 0 || pageCount <= 1) {
        setActivePage(0);
        return;
      }

      // Last page is often partial, so max scrollLeft never reaches
      // (pageCount - 1) * clientWidth — map progress across pages instead.
      const page = Math.round(
        (trackRef.scrollLeft / maxScroll) * (pageCount - 1),
      );
      setActivePage(Math.min(Math.max(page, 0), pageCount - 1));
    };

    const update = () => {
      updateOverflow();
      updateActivePage();
    };

    update();
    trackRef.addEventListener("scroll", updateActivePage, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(trackRef);

    return () => {
      trackRef.removeEventListener("scroll", updateActivePage);
      resizeObserver.disconnect();
    };
  }, [trackRef, pageCount, items.length, scrollAmount]);

  const showNav = canScroll && pageCount > 1;

  return (
    <GlassCard>
      <CarouselSectionHeader mb={2}>
        <CarouselTitleRow>
          {typeof title === "string" ? (
            <Typography variant="subtitle1" fontWeight={700}>
              {title}
            </Typography>
          ) : (
            title
          )}
        </CarouselTitleRow>

        {(actionSlot || showNav) && (
          <Stack direction="row" alignItems="center" spacing={1}>
            {actionSlot}
            {showNav && (
              <>
                <CarouselNavButton onClick={() => scrollBy("left")}>
                  <ChevronLeft />
                </CarouselNavButton>
                <CarouselNavButton onClick={() => scrollBy("right")}>
                  <ChevronRight />
                </CarouselNavButton>
              </>
            )}
          </Stack>
        )}
      </CarouselSectionHeader>
      {loading ? (
        <Box
          sx={{
            minHeight: 245,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loading />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            minHeight: 245,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <>
          <CarouselTrack ref={setTrackRef}>
            {items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  scrollSnapAlign: "start",
                  width: scrollAmount,
                  flexShrink: 0,
                }}
              >
                {item}
              </Box>
            ))}
          </CarouselTrack>
          {showNav && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              mt={2}
            >
              {Array.from({ length: pageCount }).map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor:
                      index === activePage ? "primary.main" : "transparent",
                    border: "1px solid",
                    borderColor: "primary.main",
                    opacity: index === activePage ? 1 : 0.4,
                  }}
                />
              ))}
            </Stack>
          )}
        </>
      )}
    </GlassCard>
  );
}

export default CarouselCard;
